import React, { useState } from 'react';
import { Form } from '@inertiajs/react';
import { DatePicker, TimePicker, Input, Select } from 'antd'
import { Switch } from '@/components/ui';
import OwnersMultiSelect from '@/components/forms/OwnersMultiSelect';
import CategoryMultiSelect from '@/components/forms/CategoryMultiSelect';
import { EventOwner } from '@/pages/admin/event_owners/index.page';
import { Event, Category } from '@/types/events';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;

interface EventFormProps {
  initialValues?: Event;
  categories?: Category[];
  eventOwners?: EventOwner[];
  action: string;
  method?: 'post' | 'put' | 'patch' | 'delete';
}

const EventFormInertia: React.FC<EventFormProps> = ({
  initialValues,
  categories = [],
  eventOwners = [],
  action,
  method = 'post'
}) => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialValues?.category_ids || []);
  const [selectedOwners, setSelectedOwners] = useState<string[]>(
    (initialValues?.owner_ids || []).map((id) => id.toString())
  );
  const [eventDates, setEventDates] = useState<any>(
    initialValues?.start_date && initialValues?.end_date ?
    [dayjs(initialValues.start_date), dayjs(initialValues.end_date)] : undefined
  );
  const [eventTimes, setEventTimes] = useState<any>(
    initialValues?.start_time && initialValues?.end_time ?
    [dayjs(initialValues.start_time, 'HH:mm'), dayjs(initialValues.end_time, 'HH:mm')] : undefined
  );

  console.log('##############')
  console.log(initialValues)
  console.log('##############')

  // Process dates and times for form submission
  const transformEventDates = () => {
    if (eventDates && Array.isArray(eventDates) && eventDates.length === 2) {
      const [startDate, endDate] = eventDates;
      return {
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD')
      };
    }
    return {};
  };

  const transformEventTimes = () => {
    if (eventTimes && Array.isArray(eventTimes) && eventTimes.length === 2) {
      const [startTime, endTime] = eventTimes;
      return {
        start_time: startTime.format('HH:mm'),
        end_time: endTime.format('HH:mm')
      };
    }
    return {};
  };

  return (
    <>
      <Form
        action={action}
        method={method}
        className="max-w-3xl"
        id={`event-form-${action.replace(/[^a-zA-Z0-9]/g, '-')}`}
      >
      <div className="mb-4 px-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Event Name *</label>
        <Input
          name="event[name]"
          placeholder="Enter event name"
          defaultValue={initialValues?.name}
          required
          size="small"
          className="w-full"
        />
      </div>

      <div className="mb-4 px-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
        <Input
          name="event[client_name]"
          placeholder="Enter client name"
          defaultValue={initialValues?.client_name}
          required
          size="small"
          className="w-full"
        />
      </div>

      <div className="mb-4 px-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Event Dates *</label>
        <DatePicker.RangePicker
          className="w-full"
          format="YYYY-MM-DD"
          value={eventDates}
          onChange={setEventDates}
          size="small"
          required
        />
        <input type="hidden" name="event[start_date]" value={transformEventDates().start_date || ''} />
        <input type="hidden" name="event[end_date]" value={transformEventDates().end_date || ''} />
      </div>

      <div className="mb-4 px-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Event Time</label>
        <TimePicker.RangePicker
          className="w-full"
          format="HH:mm"
          value={eventTimes}
          onChange={setEventTimes}
          size="small"
        />
        <input type="hidden" name="event[start_time]" value={transformEventTimes().start_time || ''} />
        <input type="hidden" name="event[end_time]" value={transformEventTimes().end_time || ''} />
      </div>

      <div className="mb-4 px-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
        <Input
          name="event[location]"
          placeholder="Enter event location"
          defaultValue={initialValues?.location}
          size="small"
          className="w-full"
        />
      </div>

      <div className="mb-4 px-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <TextArea
          name="event[description]"
          rows={4}
          placeholder="Enter event description"
          defaultValue={initialValues?.description}
          size="small"
          className="w-full"
        />
      </div>

      <div className="mb-4 px-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Categories</label>
        <CategoryMultiSelect
          categories={categories}
          selectedCategoryIds={selectedCategories}
          onChange={setSelectedCategories}
        />
        {selectedCategories.map(categoryId => (
          <input key={categoryId} type="hidden" name="event[category_ids][]" value={categoryId} />
        ))}
      </div>

      <div className="mb-4 px-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Event Owners</label>
        <OwnersMultiSelect
          eventOwners={eventOwners}
          selectedOwnerIds={selectedOwners}
          onChange={setSelectedOwners}
        />
        {selectedOwners.map(ownerId => (
          <input key={ownerId} type="hidden" name="event[owner_ids][]" value={ownerId} />
        ))}
      </div>

      <div className="mb-4 px-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
        <Select
          placeholder="Select status"
          defaultValue={initialValues?.status || "draft"}
          className="w-full"
          size="small"
          onChange={(value) => {
            // Update the hidden input value
            const statusInput = document.querySelector('input[name="event[status]"]') as HTMLInputElement;
            if (statusInput) statusInput.value = value;
          }}
        >
          <Option value="draft">Draft</Option>
          <Option value="live">Live</Option>
          <Option value="archived">Archived</Option>
        </Select>
        <input type="hidden" name="event[status]" value={initialValues?.status || "draft"} />
      </div>

      <div className="mb-4 px-6">
        <div className="flex items-center">
          <Switch
            defaultChecked={initialValues?.allow_public_registration || false}
            onChange={(checked) => {
              // Update the hidden input value
              const publicRegInput = document.querySelector('input[name="event[allow_public_registration]"]') as HTMLInputElement;
              if (publicRegInput) publicRegInput.value = checked ? "true" : "false";
            }}
          />
          <label className="block text-sm font-medium text-gray-700 ml-2">Allow Public Registration</label>
        </div>
        <input
          type="hidden"
          name="event[allow_public_registration]"
          value={initialValues?.allow_public_registration ? "true" : "false"}
        />
      </div>

      <div className="mb-4 px-6">
        <div className="flex items-center">
          <Switch
            defaultChecked={initialValues?.allow_attendee_app_access || false}
            onChange={(checked) => {
              // Update the hidden input value
              const appAccessInput = document.querySelector('input[name="event[allow_attendee_app_access]"]') as HTMLInputElement;
              if (appAccessInput) appAccessInput.value = checked ? "true" : "false";
            }}
          />
          <label className="block text-sm font-medium text-gray-700 ml-2">Allow Attendee App Access</label>
        </div>
        <input
          type="hidden"
          name="event[allow_attendee_app_access]"
          value={initialValues?.allow_attendee_app_access ? "true" : "false"}
        />
      </div>
      </Form>

      {/* Footer - Outside of form and max-width container */}
      <div className="mt-6 h-[64px] bg-white border-t border-[#f0f0f0] flex items-center justify-between w-full px-6">
        {/* Left side - Save Changes button */}
        <div className="flex items-center gap-[12px]">
          <button
            type="submit"
            form={`event-form-${action.replace(/[^a-zA-Z0-9]/g, '-')}`}
            className="bg-[#132b51] text-white px-[16px] py-[5px] rounded-[2px] border border-[#132b51] shadow-[0px_2px_0px_0px_rgba(0,0,0,0.04)] hover:bg-[#0f1f3c] font-['Roboto'] font-normal text-[14px] leading-[22px]"
          >
            {initialValues ? 'Save Changes' : 'Create New Event'}
          </button>
        </div>

        {/* Right side - Delete and Cancel buttons */}
        <div className="flex items-center gap-[16px] px-[24px]">
          {initialValues && (
            <button
              type="button"
              className="bg-white text-[#6e1736] px-[16px] py-[5px] rounded-[2px] border border-[#6e1736] shadow-[0px_2px_0px_0px_rgba(0,0,0,0.02)] hover:bg-red-50 font-['Roboto'] font-normal text-[14px] leading-[22px]"
              onClick={() => {
                if (initialValues?.id && confirm('Are you sure you want to delete this event?')) {
                  window.location.href = `/admin/events/${initialValues.id}`;
                }
              }}
            >
              Delete
            </button>
          )}
          <button
            type="button"
            className="bg-white text-[rgba(0,0,0,0.85)] px-[16px] py-[5px] rounded-[2px] border border-[#d9d9d9] shadow-[0px_2px_0px_0px_rgba(0,0,0,0.02)] hover:bg-gray-50 font-['Roboto'] font-normal text-[14px] leading-[22px]"
            onClick={() => window.history.back()}
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};

export default EventFormInertia;
